import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { getAllUsers, uploadAsset, updateAsset } from '../../../actions';
import StyledSelect from '../../../components/common/select';
import DragAndDrop from './DragAndDrop';

const NewAssetForm = ({ onUploadComplete, mode = 'create', initialValues = {} }) => {
  const dispatch = useDispatch();
  const users = useSelector((state) => state.viewer.allMembers);
  const uploading = useSelector((state) => state.assets.assetsModalProps.uploading);
  const uploadError = useSelector((state) => state.assets.assetsModalProps.uploadError);
  const saving = useSelector((state) => state.assets.assetsModalProps.saving);
  const updateError = useSelector((state) => state.assets.assetsModalProps.updateError);
  const [assetName, setAssetName] = useState(initialValues.name || '');
  const [selectedUsers, setSelectedUsers] = useState(
    initialValues.users_access ? initialValues.users_access.map((u) => u.user_id) : [],
  );
  const [file, setFile] = useState(null);
  const [wasUploading, setWasUploading] = useState(false);
  const [wasSaving, setWasSaving] = useState(false);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    dispatch(getAllUsers());
  }, [dispatch]);

  useEffect(() => {
    if (wasUploading && !uploading && !uploadError) {
      if (onUploadComplete) onUploadComplete();
    }
    setWasUploading(uploading);
  }, [uploading, uploadError, wasUploading, onUploadComplete]);

  useEffect(() => {
    if (wasSaving && !saving && !updateError) {
      if (onUploadComplete) onUploadComplete();
    }
    setWasSaving(saving);
  }, [saving, updateError, wasSaving, onUploadComplete]);

  const handleAssetNameChange = (e) => {
    setAssetName(e.target.value);
    setErrors({ ...errors, assetName: '' });
  };

  const handleUserChange = (selectedNames) => {
    const selectedIds = selectedNames
      .map((name) => {
        const user = users.find((u) => `${u.firstName} ${u.lastName}` === name);
        return user ? user.id : null;
      })
      .filter(Boolean);
    setSelectedUsers(selectedIds);
    setErrors({ ...errors, users: '' });
  };

  const handleFileChange = (selectedFile) => {
    setFile(selectedFile);
    setErrors({ ...errors, file: '' });
  };

  const userOptions = users.map((user) => ({
    id: user.id,
    label: `${user.firstName} ${user.lastName}`,
    name: 'users',
  }));

  const handleSubmit = (e) => {
    e.preventDefault();
    const newErrors = {};
    if (!assetName.trim()) newErrors.assetName = 'Asset name is required.';
    if (selectedUsers.length === 0) newErrors.users = 'At least one user must be selected.';
    if (mode === 'create' && !file) newErrors.file = 'A file must be selected.';
    setErrors(newErrors);
    if (Object.keys(newErrors).length > 0) return;
    if (mode === 'edit') {
      dispatch(updateAsset({ id: initialValues.id, name: assetName, users: selectedUsers, file }));
    } else {
      dispatch(uploadAsset({ file, name: assetName, users: selectedUsers }));
    }
  };

  return (
    <form className="new-asset-form" onSubmit={handleSubmit}>
      <div className="new-asset-form-body">
        <div className="new-asset-form-item">
          <input
            type="text"
            id="assetName"
            className="new-asset-input"
            value={assetName}
            onChange={handleAssetNameChange}
            placeholder="File Name"
          />
          {errors.assetName && <div className="form-error">{errors.assetName}</div>}
        </div>
        <div className="new-asset-form-item">
          <StyledSelect
            options={userOptions}
            handleChange={handleUserChange}
            value={selectedUsers.map((id) => {
              const user = users.find((u) => u.id === id);
              return user ? `${user.firstName} ${user.lastName}` : '';
            })}
            multiselect={true}
            defaultOption="File Access"
            isCreatable={false}
            showSelectAll={true}
          />
          {errors.users && <div className="form-error">{errors.users}</div>}
        </div>
        <div className="new-asset-form-item drag-drop-wrapper">
          <DragAndDrop onFile={handleFileChange} />
          {file && <p className="selected-file-name">Selected file: {file.name}</p>}
          {mode === 'edit' && !file && <p className="selected-file-name">Selected file: {initialValues.name}</p>}
          {errors.file && <div className="form-error">{errors.file}</div>}
        </div>
      </div>
      <button className="new-asset-submit-btn" type="submit" disabled={uploading || saving}>
        {uploading || saving
          ? mode === 'edit'
            ? 'Saving...'
            : 'Uploading...'
          : mode === 'edit'
            ? 'Save Changes'
            : 'Upload Asset'}
      </button>
    </form>
  );
};

export default NewAssetForm;
